import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, InputNumber, Space, message, Popconfirm, Input, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axiosInstance from '../../../configs';

const StorageRateTable = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingRecord, setEditingRecord] = useState(null);
    const [form] = Form.useForm();

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/referensi-finance/storage-rates');
            setData(res.data.data);
        } catch (err) {
            message.error('Gagal mengambil data storage');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        form.setFieldsValue({ type_storage: 0, contact_type: 0, unit_type: 'Karton' });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingId(record.id_cost_storage);
        setEditingRecord(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await axiosInstance.delete(`/referensi-finance/storage-rates/${id}`);
            message.success('Tarif dihapus');
            fetchData();
        } catch (err) {
            message.error(err.response?.data?.error || 'Gagal menghapus');
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (editingId) {
                const payload = {
                    cost_code: values.cost_code || "",
                    category_name: values.category_name,
                    category_cost: values.category_cost,
                    unit_type: values.unit_type,
                    contact_type: values.contact_type,
                    type_storage: values.type_storage
                };
                await axiosInstance.put(`/referensi-finance/storage-rates/${editingId}`, payload);
                message.success('Tarif diperbarui');
            } else {
                const payload = {
                    cost_code: values.cost_code || "",
                    category_name: values.category_name,
                    category_cost: values.category_cost,
                    unit_type: values.unit_type,
                    contact_type: values.contact_type,
                    type_storage: values.type_storage
                };
                await axiosInstance.post('/referensi-finance/storage-rates', payload);
                message.success('Tarif dibuat');
            }
            setModalVisible(false);
            fetchData();
        } catch (err) {
            message.error('Gagal menyimpan data');
        }
    };

    const columns = [
        { title: 'Cost Code', dataIndex: 'cost_code', width: 100 },
        { title: 'Kategori / Nama Barang', dataIndex: 'category_name', ellipsis: true },
        {
            title: 'Tarif (IDR)',
            dataIndex: 'category_cost',
            align: 'right',
            width: 100,
            render: (val) => val ? val.toLocaleString('id-ID') : '0'
        },
        { title: 'Satuan', dataIndex: 'unit_type', width: 80, responsive: ['sm'] },
        {
            title: 'Tipe',
            dataIndex: 'contact_type',
            width: 80,
            responsive: ['md'],
            render: (val) => val === 0 ? 'Grup' : 'Non'
        },
        {
            title: 'Aksi',
            key: 'action',
            width: 100,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
                    <Popconfirm title="Hapus tarif ini?" onConfirm={() => handleDelete(record.id_cost_storage)}>
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ marginBottom: 12 }}>
                <Button type="primary" icon={<PlusOutlined />} size="small" onClick={handleAdd}>
                    Tambah Tarif
                </Button>
            </div>
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id_cost_storage"
                loading={loading}
                size="small"
                scroll={{ x: 500 }}
                pagination={{ pageSize: 10, size: "small" }}
            />
            <Modal
                title={editingId ? `Edit Tarif Storage: ${editingRecord?.category_name}` : "Tambah Tarif Storage"}
                open={modalVisible}
                onOk={handleOk}
                onCancel={() => setModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="cost_code" label="Cost Code" rules={[{ required: true, message: 'Harap isi cost code' }]}>
                        <Input placeholder="Contoh: STOR-001" />
                    </Form.Item>
                    <Form.Item name="category_name" label="Nama Kategori / Barang" rules={[{ required: true }]}>
                        <Input placeholder="Contoh: Storage Case (75cl / 4.5L)" />
                    </Form.Item>
                    <Form.Item name="category_cost" label="Tarif (IDR)" rules={[{ required: true }]}>
                        <InputNumber
                            style={{ width: '100%' }}
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                    </Form.Item>
                    <Form.Item name="unit_type" label="Satuan" rules={[{ required: true }]}>
                        <Input placeholder="Contoh: Karton, Pallet, dll" />
                    </Form.Item>
                    <Form.Item name="contact_type" label="Tipe Kontak">
                        <Select>
                            <Select.Option value={0}>Grup</Select.Option>
                            <Select.Option value={1}>Non-Grup</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="type_storage" label="Tipe Storage" initialValue={0}>
                        <Select disabled>
                            <Select.Option value={0}>Standard</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default StorageRateTable;
